<template>
  <div class="flex justify-center items-center">
    <!-- toggle -->
    <button
      type="button"
      role="switch"
      :aria-checked="toggleBool"
      :aria-busy="loading"
      :aria-label="ariaLabel"
      class="relative"
      :class="isInteractionLocked ? 'cursor-not-allowed' : 'cursor-pointer'"
      @click.prevent="switchToggle"
      :disabled="isInteractionLocked"
    >
      <input
        :id="inputId"
        type="checkbox"
        :checked="toggleBool"
        @input="$emit('update:toggleBool', $event.target.checked)"
        :disabled="isInteractionLocked"
        class="sr-only peer"
        tabindex="-1"
      >
      <!-- path -->
      <div
        class="toggle-path rounded-full light:shadow-inner dark:border"
        :class="togglePathStyles"
      />
      <!-- circle -->
      <div
        class="toggle-circle absolute rounded-full shadow inset-y-0 flex items-center justify-center"
        :class="[circleClasses, { 'is-on': toggleBool }]"
      >
        <font-awesome-icon
          v-if="activeIcon"
          :icon="activeIcon"
          aria-hidden="true"
          class="toggle-knob-icon"
          :class="[activeIconClass, size === 'sm' ? 'w-2.5 h-2.5' : 'w-3 h-3']"
        />
      </div>
    </button>
  </div>
</template>

<script>
let toggleIdCounter = 0

export default {
  name: "VToggle",
  props: {
    toggleBool: {
      type: Boolean,
      required: true
    },
    color: {
      type: String,
      required: false,
      default: null
    },
    // Optional override for the ON track (falls back to `color`)
    onColor: {
      type: String,
      required: false,
      default: null
    },
    // Optional override for the OFF track (default gray)
    offColor: {
      type: String,
      required: false,
      default: null
    },
    // Font Awesome icon class shown inside the knob when ON, e.g. "fa-solid fa-check"
    onIcon: {
      type: String,
      required: false,
      default: null
    },
    // Font Awesome icon class shown inside the knob when OFF
    offIcon: {
      type: String,
      required: false,
      default: null
    },
    onIconClass: {
      type: String,
      required: false,
      default: "text-gray-700"
    },
    offIconClass: {
      type: String,
      required: false,
      default: "text-gray-700"
    },
    size: {
      type: String,
      default: () => "md"
    },
    disabled: {
      type: Boolean,
      default: false
    },
    // When true, keeps the toggle visible but shows a spinner in the knob and blocks interaction
    loading: {
      type: Boolean,
      default: false
    },
    ariaLabel: {
      type: String,
      required: false,
      default: "Toggle"
    }
  },
  emits: ["update:toggleBool", "switchToggle"],
  data() {
    return {
      inputId: `v-toggle-${++toggleIdCounter}`
    }
  },
  computed: {
    isInteractionLocked() {
      return this.disabled || this.loading
    },
    resolvedOnColor() {
      return this.onColor || this.color || "default"
    },
    activeIcon() {
      if (this.loading) return "fa-solid fa-circle-notch"
      return this.toggleBool ? this.onIcon : this.offIcon
    },
    activeIconClass() {
      if (this.loading) return "text-gray-700 animate-spin"
      return this.toggleBool ? this.onIconClass : this.offIconClass
    },
    togglePathStyles() {
      const on = this.toggleBool
      const onColor = this.resolvedOnColor
      const offColor = this.offColor

      return {
        // ON colors
        "bg-primary-100 dark:bg-primarydark/60 dark:border-primarydark-400/60 dark:shadow-primarydark/30": on && onColor === "default",
        "bg-primary dark:bg-primarydark/60 dark:border-primarydark-400/60 dark:shadow-primarydark/30": on && onColor === "primary",
        "bg-purple-900": on && onColor === "purple",
        "bg-gray-500 dark:bg-gray-800": on && onColor === "gray",
        "bg-green-500 dark:bg-green-500 dark:border-green-400/60": on && onColor === "green",
        "bg-amber-400 dark:bg-amber-500 dark:border-amber-300/60": on && onColor === "amber",
        "bg-yellow-400 dark:bg-yellow-500 dark:border-yellow-300/60": on && onColor === "yellow",
        "bg-red-500 dark:bg-red-500 dark:border-red-400/60": on && onColor === "red",
        // OFF colors
        "bg-gray-300 dark:bg-gray-700 dark:border-gray-500": !on && !offColor,
        "bg-amber-100 border border-amber-400 dark:bg-navy-900 dark:border-amber-400": !on && offColor === "amber",
        "bg-yellow-100 border border-yellow-400 dark:bg-navy-900 dark:border-yellow-400": !on && offColor === "yellow",
        "bg-red-100 border border-red-400 dark:bg-navy-900 dark:border-red-400": !on && offColor === "red",
        "bg-gray-200 border border-gray-400 dark:bg-gray-800 dark:border-gray-500": !on && offColor === "gray",
        "bg-green-100 border border-green-400 dark:bg-navy-900 dark:border-green-400": !on && offColor === "green",
        // Size
        "w-12 h-6": this.size === "md",
        "w-10 h-5": this.size === "sm"
      }
    },
    circleClasses() {
      return {
        "bg-gray-200 dark:bg-gray-400": this.isInteractionLocked,
        "bg-white": !this.isInteractionLocked,
        "w-5 h-5": this.size === "md",
        "w-4 h-4": this.size === "sm"
      }
    }
  },
  methods: {
    switchToggle() {
      if (!this.isInteractionLocked) this.$emit("switchToggle")
    }
  }
}
</script>

<style scoped>
.toggle-path {
  transition: background 0.2s ease-in-out, border-color 0.2s ease-in-out;
}
.toggle-circle {
  top: 50%;
  transform: translate(0, -50%);
  left: 0.2rem;
  transition: all 0.2s ease-in-out;
}
.toggle-circle.is-on {
  transform: translate(110%, -50%);
}
.toggle-knob-icon {
  pointer-events: none;
  line-height: 1;
}
</style>
